import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import { useStore } from 'react-redux'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { SectionCard } from 'src/components/shared/SectionCard'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { StepAction } from 'src/constants/partnerDriver/enums'
import { buildStepColumns } from 'src/constants/partnerDriver/stepGrid'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type {
  PartnerDriverDetails,
  PartnerDriverStep,
  StepActionPermissions,
} from 'src/interfaces/partnerDriver'
import type { State } from 'src/reducers/types'
import { getPartnerDriverSaving } from 'src/selectors/partnerDriver'
import { dedupeSteps } from 'src/utils/partnerDriver/dedupeSteps'
import { EmptySteps } from './EmptySteps'
import { StepActionModal } from './StepActionModal'

interface Props {
  driver: PartnerDriverDetails
  onChanged(): void
}

interface Selection {
  action: StepAction
  step: PartnerDriverStep
}

const getRowId = (row: PartnerDriverStep) => {
  return row.id
}

/**
 * Every non-archived step (the backend filters archived ones), deduped as Retool did and sorted
 * by template order. No pagination: a template holds a dozen steps at most, and `autoHeight`
 * keeps the page scrolling instead of an inner scroller swallowing the wheel.
 */
const StepsCardComponent = ({ driver, onChanged }: Props) => {
  const [selection, setSelection] = useState<Selection | null>(null)
  const canManageSteps = useHasPermission(InternalPermission.ContractorStepsManage)
  const canManageDocuments = useHasPermission(InternalPermission.ContractorDocumentsManage)
  const store = useStore<State>()

  // One object per permission change, so the memoized columns below stay stable between renders.
  const stepActionPermissions = useMemo<StepActionPermissions>(() => {
    return { canManageSteps, canManageDocuments }
  }, [canManageDocuments, canManageSteps])

  const steps = useMemo(() => {
    // `dedupeSteps` returns a new array, so sorting it never touches Redux state.
    return dedupeSteps(driver.collectDataSteps ?? []).sort((first, second) => {
      return first.order - second.order
    })
  }, [driver.collectDataSteps])

  const handleSelectAction = useCallback((step: PartnerDriverStep, action: StepAction) => {
    setSelection({ action, step })
  }, [])

  // Escape and the backdrop must not dismiss a step-action modal while its request is in flight.
  // Read from the store at call time, not a render-time value: the success path closes the modal
  // right after the saga's `success` put, before React has re-rendered with `saving: false`.
  const handleClose = useCallback(() => {
    if (getPartnerDriverSaving(store.getState())) {
      return
    }

    setSelection(null)
  }, [store])

  const columns = useMemo(() => {
    return buildStepColumns(handleSelectAction, stepActionPermissions)
  }, [handleSelectAction, stepActionPermissions])

  return (
    <Fragment>
      <SectionCard
        title="Collected data steps"
        subtitle={`${steps.length} ${steps.length === 1 ? 'step' : 'steps'}`}
        dataTestId="partner-driver-steps-card"
      >
        <GridTable<PartnerDriverStep>
          rows={steps}
          columns={columns}
          getRowId={getRowId}
          isStandalone
          autoHeight
          getRowHeight={() => {
            return 'auto'
          }}
          EmptyElement={EmptySteps}
          tableDataTestId="partner-driver-steps-table"
          rowDataTestIdPrefix="partner-driver-steps"
        />
      </SectionCard>
      {selection ? (
        <StepActionModal
          partnerDriverId={driver.id}
          action={selection.action}
          step={selection.step}
          onClose={handleClose}
          onChanged={onChanged}
        />
      ) : null}
    </Fragment>
  )
}

export const StepsCard = memo(StepsCardComponent)
